import { LeagueClassicPage } from './LeagueClassicPage.js';
import { LeagueMediaMini }   from './LeagueMediaMini.js';
/**
 * LeagueWK.js &#8212; WK League (South Korea Women's Football)
 * Architecture mirrors LeagueNWSL.js &#8212; responsive, CSS-injected.
 */
import {
  WK_STANDINGS_FALLBACK,
  WK_TOP_SCORERS_FALLBACK,
  WK_FIXTURES_FALLBACK,
  WK_SEASON,
  fetchLiveWKData,
} from '../../data/wkData.js';
import { injectCSS, gdHtml, formPills, fmtDate, BASE_CSS } from '../../leagueBase.js';

const STYLE_ID = 'leaguewk-styles';
const ACCENT   = '#003DA5';

function inject() {
  injectCSS(STYLE_ID, BASE_CSS + `
    .leaguewk-tab.active { border-bottom-color: ${ACCENT}; }
    .leaguewk-badge      { background: ${ACCENT}; color:#fff; }
    .leaguewk-scorer-g   { color: ${ACCENT}; }
    .leaguewk-pts        { color: ${ACCENT}; font-weight:700; }
  `);
}

function fixtureSidebar(fixtures = []) {
  const shown = [
    ...fixtures.filter(f => f.status === 'FT').slice(-3),
    ...fixtures.filter(f => f.status === 'NS').slice(0, 3),
  ];
  return shown.map(f => `
    <div class="lg-fix">
      <div class="lg-fix-date">${fmtDate(f.date)}</div>
      <div class="lg-fix-teams">
        <div class="lg-fix-team"><div class="lg-dot" style="background:${f.homeColor||ACCENT}"></div><span class="lg-fix-name">${f.home}</span></div>
        <div class="lg-fix-team"><div class="lg-dot" style="background:${f.awayColor||'#999'}"></div><span class="lg-fix-name">${f.away}</span></div>
      </div>
      <div class="lg-fix-score ${f.status==='NS'?'upcoming':''}">${f.status==='NS'?'vs':`${f.homeGoals}&#8211;${f.awayGoals}`}</div>
    </div>`).join('');
}

function scorerSidebar(scorers = []) {
  return scorers.slice(0, 6).map((s, i) => `
    <div class="lg-scorer">
      <div class="lg-scorer-rank">${i+1}</div>
      <div class="lg-av">${s.initials}</div>
      <div class="lg-scorer-info">
        <div class="lg-scorer-name">${s.name}</div>
        <div class="lg-scorer-club">${s.club}</div>
      </div>
      <div class="lg-scorer-g">${s.goals}</div>
    </div>`).join('');
}

function standingsRows(rows = []) {
  return rows.map(t => `
    <tr class="lg-zone-${t.zone||'n'}">
      <td>${t.pos}</td>
      <td class="l"><div class="lg-team-cell"><div class="lg-dot" style="background:${t.color}"></div><span class="lg-name">${t.name}</span></div></td>
      <td>${t.p}</td>
      <td class="lg-col-hide">${t.w}</td>
      <td class="lg-col-hide">${t.d}</td>
      <td class="lg-col-hide">${t.l}</td>
      <td class="lg-col-hide">${gdHtml(t.gd)}</td>
      <td class="lg-pts">${t.pts}</td>
      <td class="lg-col-hide"><div class="lg-form">${formPills(t.form||[])}</div></td>
    </tr>`).join('');
}

export class LeagueWK {
  constructor(containerId) {
    this.containerId = containerId;
    this.root        = null;
    this.data = {
      standings: WK_STANDINGS_FALLBACK,
      scorers:   WK_TOP_SCORERS_FALLBACK,
      fixtures:  WK_FIXTURES_FALLBACK,
    };
  }

  async init() {
    inject();
    this.root = document.getElementById(this.containerId);
    if (!this.root) return;
    const live = await fetchLiveWKData();
    if (live) {
      if (live.standings)  this.data.standings = live.standings;
      if (live.topScorers) this.data.scorers   = live.topScorers;
      if (live.fixtures)   this.data.fixtures  = live.fixtures;
    }
    this.render();
  }

  render() {
    this.root.innerHTML = this._html();
    this._attachListeners();
  }

  _html() {
    const { standings, scorers, fixtures } = this.data;
    return `
    <div class="lg-wrap">
      <div class="lg-hero">
        <div class="lg-logo-box" style="background:transparent;"><img src="/images/league_wk.png" style="width:40px;height:40px;object-fit:contain;display:block;"></div>
        <div class="lg-hero-text">
          <div class="lg-hero-title">WK League</div>
          <div class="lg-hero-sub">${WK_SEASON} &#183; 8 Clubs &#183; Hwacheon KSPO champions</div>
        </div>
      </div>

      <div class="lg-tabs" role="tablist">
        <button class="lg-tab leaguewk-tab active" data-tab="standings">Table</button>
        <button class="lg-tab leaguewk-tab" data-tab="scorers">Scorers</button>
        <button class="lg-tab leaguewk-tab" data-tab="clubs">Clubs</button>
        <button class="lg-tab leaguewk-tab" data-tab="media">Media</button>
        <button class="lg-tab leaguewk-tab" data-tab="classic">Classic</button>
        <button class="lg-tab leaguewk-tab" data-tab="about">About</button>
      </div>

      <div class="lg-body">
        <div class="lg-main">
          <div data-panel="standings">
            <table class="lg-table">
              <thead><tr>
                <th>#</th><th class="l">Club</th><th>P</th>
                <th class="lg-col-hide">W</th><th class="lg-col-hide">D</th><th class="lg-col-hide">L</th>
                <th class="lg-col-hide">GD</th><th>Pts</th><th class="lg-col-hide">Form</th>
              </tr></thead>
              <tbody>${standingsRows(standings)}</tbody>
            </table>
            <div class="lg-legend">
              <span class="lg-legend-dot champion"></span>Championship play-off
            </div>
          </div>
          <div data-panel="scorers" style="display:none">
            <div class="lg-scorer-list">${scorerSidebar(scorers)}</div>
          </div>
          <div data-panel="fixtures" style="display:none">
            <div class="lg-fix-list">${fixtureSidebar(fixtures)}</div>
          </div>
          <div data-panel="clubs" style="display:none"><div style="padding:12px 0;"><div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(130px,1fr));gap:10px;">${(standings||[]).map(t=>`<div style="background:var(--card-bg,#fff);border:1px solid var(--border-color,#eee);border-radius:10px;padding:14px 10px;text-align:center;"><div style="width:44px;height:44px;border-radius:50%;background:${t.color||'#888'};display:flex;align-items:center;justify-content:center;margin:0 auto 8px;font-size:12px;font-weight:700;color:#fff;">${t.short||'?'}</div><div style="font-size:11px;font-weight:600;color:var(--text-color,#111);line-height:1.3;margin-bottom:4px;">${t.name||''}</div><div style="font-size:10px;color:var(--text-secondary,#888);">#${t.pos||''} &middot; ${t.pts||0} pts</div></div>`).join('')}</div></div></div>
          <div data-panel="media" style="display:none"><div style="padding:40px 20px;text-align:center;color:var(--text-secondary,#888);"><div style="font-size:15px;font-weight:600;">Media - Coming Soon</div></div></div>
          <div data-panel="classic" style="display:none">
            <div class="wsl-panel" id="wk-panel-media"></div>
            <div class="wsl-panel" id="wk-panel-classic"></div><div style="padding:40px 20px;text-align:center;color:var(--text-secondary,#888);"><div style="font-size:15px;font-weight:600;">Classic - Coming Soon</div></div></div>

          <div data-panel="about" style="display:none"><div style="padding:14px;max-width:900px;"><div style="background:var(--card-bg,#fff);border:1px solid var(--border-color,#eee);border-radius:12px;padding:14px 16px;margin-bottom:12px;"><h4 style="margin:0 0 8px;font-size:13px;font-weight:700;color:#C60C30;">&#9632; Overview</h4><div style="font-size:12px;color:var(--text-secondary,#555);line-height:1.7;">South Korea&#39;s top women&#39;s football league, the WK League. A competitive division that has produced some of Asia&#39;s finest players and national team regulars.</div></div><div style="background:var(--card-bg,#fff);border:1px solid var(--border-color,#eee);border-radius:12px;padding:14px 16px;margin-bottom:12px;"><h4 style="margin:0 0 8px;font-size:13px;font-weight:700;color:#C60C30;">&#9632; History</h4><div style="font-size:12px;color:var(--text-secondary,#555);line-height:1.7;">The WK League has grown steadily since its formation, with clubs backed by major corporations following the South Korean model. The league feeds talent into the national team pipeline.</div></div><div style="background:var(--card-bg,#fff);border:1px solid var(--border-color,#eee);border-radius:12px;padding:14px 16px;margin-bottom:12px;"><h4 style="margin:0 0 8px;font-size:13px;font-weight:700;color:#C60C30;">&#9632; Honours</h4><div style="font-size:12px;color:var(--text-secondary,#555);line-height:1.7;"><strong>Hwacheon KSPO</strong> are the most successful club in WK League history. <strong>Incheon Hyundai Steel</strong> and <strong>Suwon</strong> have also been dominant forces.</div></div><div style="background:var(--card-bg,#fff);border:1px solid var(--border-color,#eee);border-radius:12px;padding:14px 16px;margin-bottom:12px;"><h4 style="margin:0 0 8px;font-size:13px;font-weight:700;color:#C60C30;">&#9632; Stars</h4><div style="font-size:12px;color:var(--text-secondary,#555);line-height:1.7;">Ji So-yun (Chelsea legend), Lee Geum-min, and Choe Yu-ri are among South Korea&#39;s finest exports. Several WK League players compete in the NWSL and European top divisions.</div></div><div style="background:var(--card-bg,#fff);border:1px solid var(--border-color,#eee);border-radius:12px;padding:14px 16px;margin-bottom:12px;"><h4 style="margin:0 0 8px;font-size:13px;font-weight:700;color:#C60C30;">&#9632; TV &amp; Media</h4><div style="font-size:12px;color:var(--text-secondary,#555);line-height:1.7;">Broadcast on <strong>SBS Sports</strong> and <strong>SPOTV</strong> in South Korea. The league receives solid domestic coverage as part of Korea&#39;s broader sports media landscape.</div></div><div style="background:var(--card-bg,#fff);border:1px solid var(--border-color,#eee);border-radius:12px;padding:14px 16px;margin-bottom:12px;"><h4 style="margin:0 0 8px;font-size:13px;font-weight:700;color:#C60C30;">&#9632; Asia</h4><div style="font-size:12px;color:var(--text-secondary,#555);line-height:1.7;">South Korea are regular Asian Cup contenders and World Cup participants. WK League clubs compete in the AFC Women&#39;s Club Championship, flying the flag for Korean women&#39;s football.</div></div><div style="background:var(--card-bg,#fff);border:1px solid var(--border-color,#eee);border-radius:12px;padding:14px 16px;margin-bottom:12px;"><h4 style="margin:0 0 8px;font-size:13px;font-weight:700;color:#C60C30;">&#9632; Key Facts</h4><div><span style="display:inline-block;background:var(--hover-bg,#f5f5f5);border-radius:6px;padding:3px 8px;margin:2px;font-size:11px;font-weight:600;">8 clubs</span><span style="display:inline-block;background:var(--hover-bg,#f5f5f5);border-radius:6px;padding:3px 8px;margin:2px;font-size:11px;font-weight:600;">Corporate backed</span><span style="display:inline-block;background:var(--hover-bg,#f5f5f5);border-radius:6px;padding:3px 8px;margin:2px;font-size:11px;font-weight:600;">Mar&#8211;Oct</span><span style="display:inline-block;background:var(--hover-bg,#f5f5f5);border-radius:6px;padding:3px 8px;margin:2px;font-size:11px;font-weight:600;">AFC member</span></div></div></div></div>
        </div>
      </div>
    </div>`;
  }

  _attachListeners() {
    this.root.querySelectorAll('.lg-tab').forEach(btn => {
      btn.addEventListener('click', () => {
        this.root.querySelectorAll('.lg-tab').forEach(b => b.classList.remove('active'));
        btn.classList.add('active')
        const _tt=btn.dataset.tab||'';if(_tt==='classic'){const _ce=this.root&&this.root.querySelector('#wk-panel-classic');if(_ce&&!_ce._cpm){new LeagueClassicPage(_ce,'wk').init();_ce._cpm=true;}}if(_tt==='media'){const _me=this.root&&this.root.querySelector('#wk-panel-media');if(_me&&!_me._mmm){new LeagueMediaMini(_me,'wk').init();_me._mmm=true;}};
        const tab = btn.dataset.tab;
        this.root.querySelectorAll('[data-panel]').forEach(p => {
          p.style.display = p.dataset.panel === tab ? '' : 'none';
        });
      });
    });
  }
}
